"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PORTAL_THEME } from "../../lib/portal_theme";
import Spinner from "../components/Spinner";
import useSalesAdmin from "../components/useSalesAdmin";
import { NIL_MODE_COOKIE, nilStoreFromCookie } from "../../lib/nil_fields.js";
import { DESIGN_INFO } from "../../lib/design_meta.js";

const T = PORTAL_THEME;

// Video-side design names (see lib/commitment_designs.js's VALID_DESIGNS) -> display label.
// The player's name on a print design. Older designs stored it on the entry;
// designs made in the layered editor only have it in their saved editor state
// (the render params — see app/design/adjust buildEditorState), and without
// this the card fell back to the template id twice ("condensed condensed").
function printDesignName(tmpl) {
  let first = tmpl?.firstName || "", last = tmpl?.lastName || "";
  if (!first && !last && typeof tmpl?.editorState === "string") {
    let params = tmpl.editorState;
    try { params = JSON.parse(tmpl.editorState)?.params || ""; } catch {}
    const p = new URLSearchParams(params);
    first = p.get("firstName") || ""; last = p.get("lastName") || "";
  }
  return `${first} ${last}`.trim();
}

// Every design is a 4:5 canvas. Its preview frame takes the same shape so the
// whole design shows — a fixed 200px-tall frame with objectFit: cover cut off
// its top and bottom. contain keeps anything not quite 4:5 whole as well.
const DESIGN_ASPECT = "4 / 5";

const DESIGN_LABELS = {
  classic: "Classic", identity: "Identity", split: "Split", dark: "Signature",
  bold: "Bold", compact: "Compact",
};

// The commitment video pipeline names Signature "dark"; the layered editor uses
// "signature" — same mapping AnnouncementSteps' own "Adjust Design" step link uses.
const VIDEO_TO_EDITOR_DESIGN = { dark: "signature" };

function SectionHeading({ children }) {
  return (
    <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", color: T.accentOrange, margin: "0 0 20px", textAlign: "center" }}>
      {children}
    </h2>
  );
}

// Opening a design from its section puts the flow in that section's mode:
// an NIL design's product page shows Add to Store (same cookie /nil sets),
// a Print one's shows Add to Cart.
function setNilMode(on) {
  try {
    document.cookie = on
      ? `${NIL_MODE_COOKIE}=1; path=/; samesite=lax; secure`
      : `${NIL_MODE_COOKIE}=; path=/; max-age=0; samesite=lax; secure`;
  } catch {}
}

function ActionBtn({ href, onClick, children, primary, disabled }) {
  const base = {
    display: "inline-flex", alignItems: "center", padding: "8px 14px", borderRadius: 0,
    fontSize: 12, fontWeight: 700, fontFamily: T.fontFamily, textTransform: "uppercase",
    letterSpacing: "0.8px", cursor: "pointer", textDecoration: "none", border: "none",
  };
  const style = primary
    ? { ...base, background: T.chromeGradient, color: T.chromeButtonText, boxShadow: T.chromeButtonShadow, border: T.chromeBorder }
    : { ...base, background: "transparent", color: T.textMuted, border: `1px solid ${T.cardBorder}` };
  if (href) return <a href={href} onClick={onClick} style={style}>{children}</a>;
  return <button type="button" onClick={onClick} disabled={disabled} style={disabled ? { ...style, opacity: 0.6, cursor: "default" } : style}>{children}</button>;
}

// The design's file name, above its thumbnail.
function FileName({ children }) {
  return (
    <p title={children} style={{ margin: 0, padding: "10px 12px", fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", borderBottom: `0.5px solid ${T.cardBorder}` }}>
      {children}
    </p>
  );
}

// Folders (lib/design_folders.js): each section has its own folder list, and
// a card's key (`<id>-<design>`) maps to the folder it sits in.
const FOLDER_KINDS = ["announcement", "print"];
const EMPTY_FOLDERS = { announcement: { folders: [], assign: {} }, print: { folders: [], assign: {} } };
const OPEN_FOLDER_STORAGE = "rg_designs_open_folder";
const newFolderId = () => {
  try { return crypto.randomUUID(); } catch { return `f${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`; }
};

const selectStyle = {
  padding: "7px 8px", borderRadius: 0, fontSize: 12, fontWeight: 700, fontFamily: T.fontFamily,
  textTransform: "uppercase", letterSpacing: "0.8px", cursor: "pointer",
  background: "transparent", color: T.textMuted, border: `1px solid ${T.cardBorder}`, maxWidth: "100%",
};

function FolderIcon({ size = 44 }) {
  return (
    <svg width={size} height={size * 0.8} viewBox="0 0 50 40" aria-hidden="true">
      <path d="M2 6a4 4 0 0 1 4-4h12l5 5h21a4 4 0 0 1 4 4v23a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4z"
        fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
    </svg>
  );
}

export default function DesignsPage() {
  const [authChecked,    setAuthChecked]    = useState(false);
  const [customer,       setCustomer]       = useState(null);
  const [annJobs,        setAnnJobs]        = useState([]);
  const [annLoading,     setAnnLoading]     = useState(true);
  const [uploads,        setUploads]        = useState([]);
  const [printLoading,   setPrintLoading]   = useState(true);
  const [thumbs,         setThumbs]         = useState({});
  const [thumbFailed,    setThumbFailed]    = useState({});
  const [error,          setError]          = useState("");
  const [deletingId,     setDeletingId]     = useState(null);
  const [duplicatingKey, setDuplicatingKey] = useState(null);
  const [renamingKey,    setRenamingKey]    = useState(null);
  const [hovered,        setHovered]        = useState(null);
  const [folders,        setFolders]        = useState(EMPTY_FOLDERS);
  const [openFolder,     setOpenFolder]     = useState({ announcement: null, print: null });
  const salesAdmin = useSalesAdmin();
  // /designs?nil=1 — the NIL store's Add Products: only the NIL designs, plus
  // a Create New Design card. Plain My Designs leaves NIL designs out; the
  // store page is where those are managed.
  const [nilOnly,        setNilOnly]        = useState(null);
  const [nilStore,       setNilStore]       = useState(null);   // { path, name, kind } of the store being filled
  useEffect(() => { setNilOnly(new URLSearchParams(window.location.search).get("nil") === "1"); }, []);

  const loginUrl = `/api/customer/auth/start?returnTo=${encodeURIComponent(nilOnly ? "/designs?nil=1" : "/designs")}`;

  // Wake the design compositor's Fly machine as early as possible — clicking
  // "Edit" on an announcement card sends the user straight into
  // /design/adjust, which needs the compositor immediately for its first
  // render. A suspended machine's wake-up can itself time out under load
  // (confirmed live: a request landed exactly in that failure window and
  // hung until Vercel's own 290s ceiling killed it with a raw 500 instead of
  // ever reaching the editor). This page is the natural earliest point
  // before that click — same fire-and-forget pattern as
  // announcement/upload/page.jsx's own wake call. See
  // app/api/design/wake-compositor/route.js.
  useEffect(() => {
    try {
      fetch("/api/design/wake-compositor", { method: "POST" }).catch(() => {});
    } catch {}
  }, []);

  // Auth
  useEffect(() => {
    if (nilOnly === null) return;
    let cancelled = false;
    (async () => {
      try {
        const res  = await fetch("/api/customer/me", { cache: "no-store" });
        const data = await res.json();
        if (cancelled) return;
        if (!data?.authenticated || data?.guest) { window.location.href = loginUrl; return; }
        setCustomer(data.customer || null);
        setAuthChecked(true);
      } catch { if (!cancelled) window.location.href = loginUrl; }
    })();
    return () => { cancelled = true; };
  }, [nilOnly]);

  // NIL view: link back to the store — the team store Add Products came
  // from (NIL_STORE_COOKIE), else their NIL store.
  useEffect(() => {
    if (!authChecked || !nilOnly) return;
    const slug = nilStoreFromCookie();
    fetch(`/api/nil/store${slug ? `?slug=${encodeURIComponent(slug)}` : ""}`, { cache: "no-store", credentials: "include" })
      .then((r) => r.json()).then((d) => setNilStore(d?.store || null)).catch(() => {});
  }, [authChecked, nilOnly]);

  // Load announcement jobs
  useEffect(() => {
    if (!authChecked || nilOnly) return;
    let cancelled = false;
    (async () => {
      try {
        setAnnLoading(true);
        const res  = await fetch("/api/commitment/list", { cache: "no-store", credentials: "include" });
        if (res.status === 401) { window.location.href = loginUrl; return; }
        const data = await res.json();
        if (!cancelled) setAnnJobs(data?.jobs || []);
      } catch {}
      finally { if (!cancelled) setAnnLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [authChecked]);

  // Load print designs (composite uploads)
  useEffect(() => {
    if (!authChecked) return;
    let cancelled = false;
    (async () => {
      try {
        setPrintLoading(true);
        const res  = await fetch("/api/uploads/list", { cache: "no-store", credentials: "include" });
        if (res.status === 401) { window.location.href = loginUrl; return; }
        const data = await res.json();
        if (!cancelled) setUploads(data?.uploads || []);
      } catch {}
      finally { if (!cancelled) setPrintLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [authChecked]);

  // Folders — not in the NIL view, which has its own short list.
  useEffect(() => {
    if (!authChecked || nilOnly) return;
    let cancelled = false;
    fetch("/api/designs/folders", { cache: "no-store", credentials: "include" })
      .then(r => r.json()).then(d => { if (!cancelled && d?.ok && d.folders) setFolders(d.folders); }).catch(() => {});
    // The folder open before leaving for the editor is open again on Back.
    try {
      const saved = JSON.parse(sessionStorage.getItem(OPEN_FOLDER_STORAGE) || "null");
      if (saved && typeof saved === "object") setOpenFolder({ announcement: saved.announcement || null, print: saved.print || null });
    } catch {}
    return () => { cancelled = true; };
  }, [authChecked, nilOnly]);

  const openFolderIn = (kind, folderId) => {
    setOpenFolder(prev => {
      const next = { ...prev, [kind]: folderId };
      try { sessionStorage.setItem(OPEN_FOLDER_STORAGE, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Every change saves the whole document; the page's copy is the truth.
  const saveFolders = async (next) => {
    setFolders(next);
    try {
      const res = await fetch("/api/designs/folders", {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ folders: next }),
      });
      if (!res.ok) setError("Failed to save folders.");
    } catch { setError("Failed to save folders."); }
  };

  const folderById = (kind, id) => (id && folders[kind]?.folders.find(f => f.id === id)) || null;
  const folderOf = (kind, key) => folderById(kind, folders[kind]?.assign?.[key])?.id || null;
  // The open folder, if it still exists; null = the section's top level.
  const openFolderId = (kind) => folderById(kind, openFolder[kind])?.id || null;
  // A card shows only in the folder it belongs to (top level = no folder).
  const inOpenFolder = (kind, key) => folderOf(kind, key) === openFolderId(kind);

  const handleNewFolder = (kind) => {
    const input = window.prompt("New folder name", "");
    const name = (input || "").replace(/\s+/g, " ").trim();
    if (!name) return;
    const id = newFolderId();
    saveFolders({ ...folders, [kind]: { ...folders[kind], folders: [...folders[kind].folders, { id, name }] } });
  };

  const handleRenameFolder = (kind, folder) => {
    const input = window.prompt("Rename folder", folder.name);
    const name = (input || "").replace(/\s+/g, " ").trim();
    if (!name || name === folder.name) return;
    saveFolders({ ...folders, [kind]: { ...folders[kind],
      folders: folders[kind].folders.map(f => f.id === folder.id ? { ...f, name } : f) } });
  };

  const handleDeleteFolder = (kind, folder) => {
    const section = kind === "announcement" ? "Announcement Graphics" : "Print Graphics";
    if (!window.confirm(`Delete the folder "${folder.name}"? The designs in it move back to ${section}; none are deleted.`)) return;
    const assign = Object.fromEntries(Object.entries(folders[kind].assign).filter(([, id]) => id !== folder.id));
    saveFolders({ ...folders, [kind]: { folders: folders[kind].folders.filter(f => f.id !== folder.id), assign } });
    if (openFolder[kind] === folder.id) openFolderIn(kind, null);
  };

  const moveToFolder = (kind, key, folderId) => {
    const assign = { ...folders[kind].assign };
    if (folderId) assign[key] = folderId; else delete assign[key];
    saveFolders({ ...folders, [kind]: { ...folders[kind], assign } });
  };

  // Deleting a design drops its folder entries (keys are `<id>-<design>`).
  const forgetFolderEntries = (kind, id) => {
    const assign = folders[kind]?.assign || {};
    if (!Object.keys(assign).some(k => k.startsWith(`${id}-`))) return;
    saveFolders({ ...folders, [kind]: { ...folders[kind],
      assign: Object.fromEntries(Object.entries(assign).filter(([k]) => !k.startsWith(`${id}-`))) } });
  };

  // Load thumbnails for print designs — one per DESIGN (an upload can hold
  // several), keyed `<uploadId>-<design>`. Each asks for that design's own
  // preview; asking per upload got the upload's photo, so a card never
  // showed the design or any change made to it. The design's updatedAt is
  // the cache-buster, so a re-saved design loads its new preview.
  useEffect(() => {
    if (!authChecked || !uploads?.length) return;
    let cancelled = false;
    (async () => {
      const next = {};
      const cards = uploads.flatMap(u => Object.entries(u?.outputs?.composite || {})
        .filter(([, t]) => t?.r2Key)
        .map(([design, t]) => ({ id: u.id, design, v: t.updatedAt || t.r2Key })));
      await Promise.all(cards.map(async ({ id, design, v }) => {
        const key = `${id}-${design}`;
        if (!id || thumbs[key]?.v === v) { if (thumbs[key]) next[key] = thumbs[key]; return; }
        try {
          const r = await fetch(`/api/uploads/${encodeURIComponent(id)}/thumbnail?preferDesign=1&design=${encodeURIComponent(design)}`, { cache: "no-store", credentials: "include" });
          if (!r.ok) return;
          const j = await r.json().catch(() => null);
          if (j?.ok && j.thumbnailUrl) {
            const raw = j.thumbnailUrl;
            next[key] = { v, url: raw + (raw.includes("?") ? "&" : "?") + "v=" + encodeURIComponent(v) };
          }
        } catch {}
      }));
      if (!cancelled) setThumbs(prev => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [authChecked, uploads]);

  // Print designs = composite uploads with at least one composite output;
  // the ones made in NIL mode (or listed in the NIL store) show only in the
  // NIL view (/designs?nil=1).
  const compositeDesigns = useMemo(() =>
    (uploads || []).filter(u =>
      u.processing_status === "done" &&
      Object.values(u.outputs?.composite || {}).some(t => t?.r2Key)
    ),
    [uploads]
  );
  const printDesigns = useMemo(() => compositeDesigns.filter(u => !u.nil), [compositeDesigns]);
  const nilDesigns   = useMemo(() => compositeDesigns.filter(u => u.nil),  [compositeDesigns]);

  // Every card's key per section — what a folder's count is taken from.
  const cardKeys = useMemo(() => ({
    announcement: annJobs.flatMap(job => {
      const designs = Object.entries(job.designs || {}).filter(([, d]) => d.hasDesign).map(([design]) => `${job.id}-${design}`);
      return designs.length ? designs : [`${job.id}-pending`];
    }),
    print: printDesigns.flatMap(u => Object.entries(u.outputs?.composite || {})
      .filter(([, t]) => t?.r2Key).map(([tmplId]) => `${u.id}-${tmplId}`)),
  }), [annJobs, printDesigns]);

  const handleDeleteCommitment = async (id) => {
    if (!window.confirm("Delete this announcement graphic? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      const res = await fetch(`/api/commitment/${encodeURIComponent(id)}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { setAnnJobs(prev => prev.filter(j => j.id !== id)); forgetFolderEntries("announcement", id); }
      else setError("Failed to delete announcement graphic.");
    } catch { setError("Failed to delete."); }
    finally { setDeletingId(null); }
  };

  const handleDeletePrint = async (id) => {
    if (!window.confirm("Delete this print graphic? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      const res = await fetch(`/api/uploads/${encodeURIComponent(id)}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { setUploads(prev => prev.filter(u => u.id !== id)); forgetFolderEntries("print", id); setThumbs(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith(`${id}-`)))); }
      else setError("Failed to delete print graphic.");
    } catch { setError("Failed to delete."); }
    finally { setDeletingId(null); }
  };

  // Duplicate copies one design into a new job/upload (see
  // lib/duplicate_design.js) and opens the COPY in the editor — the editor
  // autosaves to whatever design it has open, so editing the original and
  // "saving as new" afterwards would already have overwritten it.
  // The copy needs its own file name, different from the original's.
  const handleDuplicate = async (kind, id, design, editorDesign, currentName, nil = false) => {
    let message = "Name the duplicate file";
    let name = "";
    for (;;) {
      const input = window.prompt(message, name || currentName);
      if (input == null) return;
      name = input.replace(/\s+/g, " ").trim();
      if (!name) message = "Enter a file name for the duplicate";
      else if (name === currentName) message = "Enter a different file name from the original";
      else break;
    }
    const key = `${id}-${design}`;
    setDuplicatingKey(key);
    setError("");
    try {
      const base = kind === "announcement" ? "/api/commitment" : "/api/uploads";
      const res = await fetch(`${base}/${encodeURIComponent(id)}/duplicate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ design, name }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.id) { setError(data?.message || "Failed to duplicate design."); setDuplicatingKey(null); return; }
      // The copy goes in the original's folder (NIL cards have none).
      const folderKind = kind === "announcement" ? "announcement" : "print";
      const folderId = !nil && folderOf(folderKind, key);
      if (folderId) {
        await saveFolders({ ...folders, [folderKind]: { ...folders[folderKind],
          assign: { ...folders[folderKind].assign, [`${data.id}-${design}`]: folderId } } });
      }
      if (kind === "print") setNilMode(nil);
      window.location.href = kind === "announcement"
        ? `/design/adjust?jobId=${data.id}&design=${encodeURIComponent(editorDesign)}&flow=announcement`
        : `/design/adjust?uploadId=${encodeURIComponent(data.id)}&design=${encodeURIComponent(editorDesign)}&flow=print-composite`;
    } catch { setError("Failed to duplicate design."); setDuplicatingKey(null); }
  };

  // Rename File — the name shown above the card. Clearing the prompt goes
  // back to the default name (see lib/design_names.js).
  const handleRename = async (kind, id, design, currentName) => {
    const input = window.prompt("Rename file", currentName);
    if (input == null) return;
    const name = input.trim();
    if (name === currentName) return;
    const key = `${id}-${design}`;
    setRenamingKey(key);
    setError("");
    try {
      const base = kind === "announcement" ? "/api/commitment" : "/api/uploads";
      const res = await fetch(`${base}/${encodeURIComponent(id)}/rename`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ design, name }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) { setError(data?.message || "Failed to rename file."); return; }
      const saved = data.name || null;
      if (kind === "announcement") {
        setAnnJobs(prev => prev.map(j => j.id !== id ? j : {
          ...j, designs: { ...j.designs, [design]: { ...j.designs[design], fileName: saved } },
        }));
      } else {
        setUploads(prev => prev.map(u => {
          if (u.id !== id) return u;
          const designNames = { ...(u.designNames || {}) };
          if (saved) designNames[design] = saved; else delete designNames[design];
          return { ...u, designNames };
        }));
      }
    } catch { setError("Failed to rename file."); }
    finally { setRenamingKey(null); }
  };

  const renameLabel = (key) => renamingKey === key
    ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Spinner size={12} />Renaming…</span>
    : "Rename File";

  const duplicateLabel = (key) => duplicatingKey === key
    ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Spinner size={12} />Duplicating…</span>
    : "Duplicate";

  if (!authChecked) {
    return (
      <div style={{ minHeight: "100vh", background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: T.fontFamily }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}><Spinner size={18} /><p style={{ color: T.textMuted, fontSize: 13, margin: 0 }}>Checking login…</p></div>
      </div>
    );
  }

  const cardStyle = (id) => ({
    border: T.cardEdge, borderRadius: 0, overflow: "hidden",
    background: T.cardBackground, position: "relative",
    transition: "transform 0.18s, box-shadow 0.18s",
    transform: hovered === id ? "translateY(-3px)" : "translateY(0)",
    boxShadow: hovered === id ? "0 0 28px 8px rgba(200,200,200,0.65)" : "0 0 18px 4px rgba(200,200,200,0.45)",
  });

  // Above a section's cards: its folders as tiles at the top level, or the
  // open folder's name and controls inside one.
  const renderFolderBar = (kind, sectionLabel) => {
    const list = folders[kind]?.folders || [];
    const open = folderById(kind, openFolderId(kind));
    if (open) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
          <ActionBtn onClick={() => openFolderIn(kind, null)}>← All {sectionLabel}</ActionBtn>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 800, color: T.text, minWidth: 0 }}>
            <FolderIcon size={22} /><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{open.name}</span>
          </span>
          <span style={{ flex: 1 }} />
          <ActionBtn onClick={() => handleRenameFolder(kind, open)}>Rename Folder</ActionBtn>
          <ActionBtn onClick={() => handleDeleteFolder(kind, open)}>Delete Folder</ActionBtn>
        </div>
      );
    }
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: list.length ? 12 : 0 }}>
          <ActionBtn onClick={() => handleNewFolder(kind)}>+ New Folder</ActionBtn>
        </div>
        {list.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
            {list.map(f => {
              const key = `folder-${f.id}`;
              const count = cardKeys[kind].filter(k => folderOf(kind, k) === f.id).length;
              return (
                <button key={key} type="button" onClick={() => openFolderIn(kind, f.id)}
                  onMouseEnter={() => setHovered(key)} onMouseLeave={() => setHovered(null)}
                  style={{ ...cardStyle(key), display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", cursor: "pointer", color: T.text, fontFamily: T.fontFamily, textAlign: "left" }}>
                  <span style={{ color: T.accentOrange, flexShrink: 0, display: "flex" }}><FolderIcon size={30} /></span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                    <span style={{ display: "block", fontSize: 11, color: T.textMuted, marginTop: 2 }}>{count} {count === 1 ? "design" : "designs"}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // "Move to folder" on a card, once the section has any folders.
  const renderMove = (kind, key) => {
    const list = folders[kind]?.folders || [];
    if (!list.length) return null;
    return (
      <select aria-label="Move to folder" value={folderOf(kind, key) || ""} onChange={(e) => moveToFolder(kind, key, e.target.value || null)} style={selectStyle}>
        <option value="">{folderOf(kind, key) ? "Remove from folder" : "Move to folder…"}</option>
        {list.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
      </select>
    );
  };

  const emptyFolder = (
    <div style={{ border: T.cardEdge, borderRadius: 0, background: T.cardBackground, padding: "36px 24px", textAlign: "center" }}>
      <p style={{ color: T.textMuted, fontSize: 14, margin: 0 }}>This folder is empty. Use “Move to folder” on a design to add it here.</p>
    </div>
  );

  // Print Graphics and the NIL view — the same print-design cards. NIL ones
  // open in NIL mode (setNilMode), so Use Design lands on Add to Store.
  const renderPrintSection = ({ title, designs, nil, empty, leadCard }) => (
    <section>
      {title && <SectionHeading>{title}</SectionHeading>}
      {!nil && !printLoading && designs.length > 0 && renderFolderBar("print", "Print Graphics")}
      {printLoading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}><Spinner size={18} /><p style={{ color: T.textMuted, fontSize: 13, margin: 0 }}>Loading…</p></div>
      ) : designs.length === 0 && !leadCard ? (
        <div style={{ border: T.cardEdge, borderRadius: 0, background: T.cardBackground, boxShadow: "none", padding: "48px 24px", textAlign: "center" }}>
          <p style={{ color: T.textMuted, fontSize: 14, margin: 0 }}>{empty}</p>
        </div>
      ) : !nil && openFolderId("print") && !cardKeys.print.some(k => inOpenFolder("print", k)) ? emptyFolder : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16, justifyItems: "center" }}>
          {leadCard}
          {designs.map((u) => {
            const id = u.id;

            const compositeEntries = Object.entries(u.outputs?.composite || {}).filter(([, t]) => t?.r2Key);
            return compositeEntries.map(([tmplId, tmpl]) => {
              const key = `${id}-${tmplId}`;
              if (!nil && !inOpenFolder("print", key)) return null;
              const thumbUrl = thumbs[key]?.url || "";
              const displayName = printDesignName(tmpl);
              const label = tmpl.fullService ? "Artist design" : (DESIGN_INFO[tmplId]?.name || tmplId);
              const fileName = u.designNames?.[tmplId] || (displayName ? `${displayName} ${label}` : label);
              return (
                <div key={key} style={{ ...cardStyle(key), width: "100%" }} onMouseEnter={() => setHovered(key)} onMouseLeave={() => setHovered(null)}>
                  <FileName>{fileName}</FileName>
                  <div style={{ aspectRatio: DESIGN_ASPECT, background: T.pageBackground, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                    {thumbUrl && !thumbFailed[key] ? (
                      <img src={typeof window !== "undefined" && thumbUrl.startsWith("/") ? window.location.origin + thumbUrl : thumbUrl}
                        alt={nil ? "NIL graphic" : "Print graphic"} style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                        onError={() => setThumbFailed(prev => ({ ...prev, [key]: true }))} />
                    ) : (
                      <p style={{ color: T.textMuted, fontSize: 12, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>{thumbFailed[key] ? "Image unavailable" : <><Spinner size={12} />Loading…</>}</p>
                    )}
                  </div>
                  <div style={{ padding: "10px 12px 12px" }}>
                    <p style={{ margin: "0 0 8px", fontSize: 12, color: T.textMuted, fontWeight: 600 }}>{displayName ? `${displayName} · ${label}` : label}</p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {/* An artist-made Full Service design has no editor state. */}
                      {!tmpl.fullService && (
                        <ActionBtn primary onClick={() => setNilMode(nil)} href={`/design/adjust?uploadId=${encodeURIComponent(id)}&design=${encodeURIComponent(tmplId)}&flow=print-composite`}>Edit →</ActionBtn>
                      )}
                      {/* Only for a design that actually reached the product
                          page — usedAt is written by "Use This Design" and
                          never by Save Design or the autosave. */}
                      {tmpl.usedAt && (
                        <ActionBtn onClick={() => setNilMode(nil)} href={`/uploads/${encodeURIComponent(id)}/preview?flow=composite`
                          + `&r2_key=${encodeURIComponent(tmpl.r2Key)}`
                          + (tmpl.fadedR2Key ? `&faded_r2_key=${encodeURIComponent(tmpl.fadedR2Key)}` : "")
                          + (tmpl.productColor ? `&product_color=${encodeURIComponent(tmpl.productColor)}` : "")
                          + `&design=${encodeURIComponent(tmplId)}`
                          + (nil ? "&nil=1" : "")}>Use Design</ActionBtn>
                      )}
                      <ActionBtn onClick={() => handleRename("print", id, tmplId, fileName)} disabled={!!renamingKey}>
                        {renameLabel(key)}
                      </ActionBtn>
                      {!tmpl.fullService && (
                        <ActionBtn onClick={() => handleDuplicate("print", id, tmplId, tmplId, fileName, nil)} disabled={!!duplicatingKey}>
                          {duplicateLabel(key)}
                        </ActionBtn>
                      )}
                      {!nil && renderMove("print", key)}
                      <ActionBtn onClick={() => handleDeletePrint(id)} disabled={deletingId === id}>
                        {deletingId === id ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Spinner size={12} />Deleting…</span> : "Delete"}
                      </ActionBtn>
                    </div>
                  </div>
                </div>
              );
            });
          })}
        </div>
      )}
    </section>
  );

  return (
    <div style={{ minHeight: "100vh", background: "transparent", fontFamily: T.fontFamily, color: T.text }}>

      <div style={{ maxWidth: 1060, margin: "0 auto", padding: "56px 24px", position: "relative", zIndex: 1 }}>

        {/* Header */}
        <div className="portal-fade-up" style={{ textAlign: "center", marginBottom: 56, animationDelay: "0ms" }}>
          <h1 style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight: T.headingWeight, margin: "0 0 4px", letterSpacing: "-1px", fontFamily: T.fontFamilyHeadings, textTransform: "uppercase" }}>
            {nilOnly ? (nilStore?.kind === "team" ? "My Store Designs" : "My NIL Designs") : "My Designs"}
          </h1>
          {nilOnly ? (
            <>
              <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>Pick a design to add to your store, or create a new one.</p>
              {nilStore && <div style={{ marginTop: 14 }}><ActionBtn href={nilStore.path}>← Back to {nilStore.kind === "team" ? nilStore.name : "My Store"}</ActionBtn></div>}
            </>
          ) : (
            <>
              {customer?.email && <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>{customer.email}</p>}
              {salesAdmin && <div style={{ marginTop: 14 }}><ActionBtn href="/admin">Admin</ActionBtn></div>}
            </>
          )}
        </div>

        {error && <div style={{ color: T.error, fontSize: 14, background: "rgba(239,68,68,0.08)", padding: "12px 16px", borderRadius: 0, border: "1px solid rgba(239,68,68,0.2)", marginBottom: 24 }}>{error}</div>}

        {nilOnly ? (
          /* ── NIL designs (the store's Add Products) ── */
          renderPrintSection({ designs: nilDesigns, nil: true, leadCard: (
            <a key="create" href="/nil?go=design"
              style={{ ...cardStyle("create"), width: "100%", minHeight: 300, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, textDecoration: "none", color: T.text }}
              onMouseEnter={() => setHovered("create")} onMouseLeave={() => setHovered(null)}>
              <span style={{ width: 64, height: 64, borderRadius: "50%", border: `1.5px solid ${T.cardBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 34, fontWeight: 300, lineHeight: 1 }}>+</span>
              <span style={{ fontSize: 15, fontWeight: 800, textTransform: "uppercase", letterSpacing: "1px" }}>Create New Design</span>
              <span style={{ fontSize: 12, color: T.textMuted, padding: "0 20px", textAlign: "center" }}>Build a design for t-shirts, sweatshirts, hoodies, stickers and magnets</span>
            </a>
          ) })
        ) : (<>
        {/* ── Announcement Graphics ── */}
        <section style={{ marginBottom: 64 }}>
          <SectionHeading>Announcement Graphics</SectionHeading>
          {!annLoading && annJobs.length > 0 && renderFolderBar("announcement", "Announcement Graphics")}
          {annLoading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}><Spinner size={18} /><p style={{ color: T.textMuted, fontSize: 13, margin: 0 }}>Loading…</p></div>
          ) : annJobs.length === 0 ? (
            <div style={{ border: T.cardEdge, borderRadius: 0, background: T.cardBackground, boxShadow: "none", padding: "48px 24px", textAlign: "center" }}>
              <p style={{ color: T.textMuted, fontSize: 14, margin: 0 }}>No announcement graphics yet.{" "}<a href="/templates?flow=announcement" style={{ color: T.accentOrange, fontWeight: 700, textDecoration: "none" }}>Create one →</a></p>
            </div>
          ) : openFolderId("announcement") && !cardKeys.announcement.some(k => inOpenFolder("announcement", k)) ? emptyFolder : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16, justifyItems: "center" }}>
              {annJobs.flatMap(job => {
                const name = `${job.first_name || ""} ${job.last_name || ""}`.trim() || "Announcement";
                const designEntries = Object.entries(job.designs || {}).filter(([, d]) => d.hasDesign);
                // If no designs generated yet, show a single "continue" card for the job
                if (designEntries.length === 0) {
                  const key = `${job.id}-pending`;
                  if (!inOpenFolder("announcement", key)) return [];
                  return [(
                    <div key={key} style={{ ...cardStyle(key), width: "100%" }} onMouseEnter={() => setHovered(key)} onMouseLeave={() => setHovered(null)}>
                      <div style={{ aspectRatio: DESIGN_ASPECT, background: T.pageBackground, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <p style={{ color: T.textMuted, fontSize: 12, margin: 0 }}>Designs not yet generated</p>
                      </div>
                      <div style={{ padding: "10px 12px 12px" }}>
                        <p style={{ margin: "0 0 8px", fontSize: 12, color: T.textMuted, fontWeight: 600 }}>{name}</p>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <ActionBtn primary href={`/announcement/remove-bg?id=${job.id}`}>Continue →</ActionBtn>
                          {renderMove("announcement", key)}
                          <ActionBtn onClick={() => handleDeleteCommitment(job.id)} disabled={deletingId === job.id}>
                            {deletingId === job.id ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Spinner size={12} />Deleting…</span> : "Delete"}
                          </ActionBtn>
                        </div>
                      </div>
                    </div>
                  )];
                }
                return designEntries.map(([design, d]) => {
                  const key = `${job.id}-${design}`;
                  if (!inOpenFolder("announcement", key)) return null;
                  const fileName = d.fileName || `${name} ${DESIGN_LABELS[design] || design}`;
                  return (
                    <div key={key} style={{ ...cardStyle(key), width: "100%" }} onMouseEnter={() => setHovered(key)} onMouseLeave={() => setHovered(null)}>
                      <FileName>{fileName}</FileName>
                      <div style={{ aspectRatio: DESIGN_ASPECT, background: T.pageBackground, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                        {d.graphicUrl ? (
                          <img src={d.graphicUrl} alt={name} style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
                        ) : (
                          <p style={{ color: T.textMuted, fontSize: 12, margin: 0 }}>{d.hasVideo ? "Video ready" : "No preview yet"}</p>
                        )}
                      </div>
                      <div style={{ padding: "10px 12px 12px" }}>
                        <p style={{ margin: "0 0 8px", fontSize: 12, color: T.textMuted, fontWeight: 600 }}>{name} · {DESIGN_LABELS[design] || design}</p>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <ActionBtn primary href={`/design/adjust?jobId=${job.id}&design=${encodeURIComponent(VIDEO_TO_EDITOR_DESIGN[design] || design)}&flow=announcement`}>Edit →</ActionBtn>
                          {/* Only once this design has actually been through the
                              video step — that page is where it gets bought, and
                              the design name here is already the video-side one. */}
                          {d.hasVideo && (
                            <ActionBtn href={`/announcement/video?id=${job.id}&design=${encodeURIComponent(design)}`}>Preview Video</ActionBtn>
                          )}
                          <ActionBtn onClick={() => handleRename("announcement", job.id, design, fileName)} disabled={!!renamingKey}>
                            {renameLabel(key)}
                          </ActionBtn>
                          <ActionBtn onClick={() => handleDuplicate("announcement", job.id, design, VIDEO_TO_EDITOR_DESIGN[design] || design, fileName)} disabled={!!duplicatingKey}>
                            {duplicateLabel(key)}
                          </ActionBtn>
                          {renderMove("announcement", key)}
                          <ActionBtn onClick={() => handleDeleteCommitment(job.id)} disabled={deletingId === job.id}>
                            {deletingId === job.id ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Spinner size={12} />Deleting…</span> : "Delete"}
                          </ActionBtn>
                        </div>
                      </div>
                    </div>
                  );
                });
              })}
            </div>
          )}
        </section>

        {/* ── Print Graphics ── (NIL designs live on the NIL store's Add Products) */}
        {renderPrintSection({ title: "Print Graphics", designs: printDesigns, nil: false,
          empty: <>No print graphics yet.{" "}<a href="/templates?flow=print-composite" onClick={() => setNilMode(false)} style={{ color: T.accentOrange, fontWeight: 700, textDecoration: "none" }}>Create one →</a></> })}
        </>)}
      </div>
    </div>
  );
}
